import { bus } from '@/core/EventBus';
import type { CutscenePlayer } from '@/gameplay/Cutscene';

export class CutsceneOverlay {
  readonly #root: HTMLDivElement;
  readonly #speaker: HTMLDivElement;
  readonly #line: HTMLDivElement;
  readonly #skip: HTMLDivElement;
  readonly #unsubscribers: Array<() => void> = [];

  constructor(container: HTMLElement, private readonly player: CutscenePlayer) {
    this.#root = document.createElement('div');
    this.#root.className = 'na-cutscene';
    this.#root.innerHTML = `
      <div class="na-cutscene-bars" aria-hidden="true"></div>
      <section class="na-cutscene-text" aria-live="polite">
        <div class="na-cutscene-speaker"></div>
        <div class="na-cutscene-line"></div>
      </section>
      <div class="na-cutscene-skip">Esc skip</div>
    `;
    installCutsceneStyles(container.ownerDocument);
    container.appendChild(this.#root);
    this.#speaker = this.#query('.na-cutscene-speaker');
    this.#line = this.#query('.na-cutscene-line');
    this.#skip = this.#query('.na-cutscene-skip');
    this.#wireEvents();
    this.update();
  }

  update(): void {
    const text = this.player.text;
    this.#root.classList.toggle('is-visible', this.player.active);
    this.#root.classList.toggle('has-text', text !== null);
    this.#speaker.textContent = text?.speaker ?? '';
    this.#line.textContent = text?.line ?? '';
    this.#skip.hidden = !this.player.active;
  }

  dispose(): void {
    for (const unsubscribe of this.#unsubscribers) unsubscribe();
    this.#unsubscribers.length = 0;
    this.#root.remove();
  }

  #wireEvents(): void {
    this.#unsubscribers.push(
      bus.on('cutscene:beat', () => this.update()),
      bus.on('cutscene:started', () => this.update()),
      bus.on('cutscene:ended', () => this.update()),
      bus.on('cutscene:skipped', () => this.update()),
    );
  }

  #query<T extends HTMLElement>(selector: string): T {
    const found = this.#root.querySelector<T>(selector);
    if (!found) throw new Error(`Cutscene element missing: ${selector}`);
    return found;
  }
}

function installCutsceneStyles(document: Document): void {
  if (document.getElementById('na-cutscene-styles')) return;
  const style = document.createElement('style');
  style.id = 'na-cutscene-styles';
  style.textContent = `
    .na-cutscene {
      position: absolute;
      inset: 0;
      pointer-events: none;
      opacity: 0;
      color: #e8ddc8;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      transition: opacity 180ms ease;
    }
    .na-cutscene.is-visible {
      opacity: 1;
    }
    .na-cutscene-bars::before,
    .na-cutscene-bars::after {
      content: "";
      position: absolute;
      left: 0;
      width: 100%;
      height: 10vh;
      background: rgba(8, 8, 10, 0.88);
    }
    .na-cutscene-bars::before {
      top: 0;
    }
    .na-cutscene-bars::after {
      bottom: 0;
    }
    .na-cutscene-text {
      position: absolute;
      left: 50%;
      bottom: 7vh;
      width: min(720px, calc(100vw - 36px));
      min-height: 76px;
      padding: 12px 16px;
      transform: translateX(-50%);
      border-left: 3px solid #e0951c;
      background: rgba(8, 8, 10, 0.78);
      opacity: 0;
    }
    .na-cutscene.has-text .na-cutscene-text {
      opacity: 1;
    }
    .na-cutscene-speaker {
      min-height: 16px;
      color: #e0951c;
      font-size: 12px;
      line-height: 1;
      text-transform: uppercase;
    }
    .na-cutscene-line {
      margin-top: 7px;
      font-size: 15px;
      line-height: 1.35;
    }
    .na-cutscene-skip {
      position: absolute;
      right: 18px;
      top: calc(10vh + 12px);
      padding: 6px 8px;
      background: rgba(8, 8, 10, 0.62);
      border: 1px solid rgba(232, 221, 200, 0.24);
      color: rgba(232, 221, 200, 0.78);
      font-size: 11px;
      text-transform: uppercase;
    }
  `;
  document.head.appendChild(style);
}
